// ─── Workshop Truck Availability Tracker ───
// AvailabilityReport – Historical fleet availability analysis with breakdown views
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useMemo } from 'react'
import { format, subDays, startOfMonth } from 'date-fns'
import {
  TrendingUp,
  TrendingDown,
  Award,
  Loader2,
} from 'lucide-react'
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ReferenceLine,
  Legend,
} from 'recharts'

import type { AvailabilitySnapshot, Truck } from '@/types'
import { useTrucks } from '@/hooks/useTrucks'
import { getAvailabilityColor, formatDate, normalizeMake } from '@/utils/helpers'

// ─── Props ──────────────────────────────────────────────────

interface AvailabilityReportProps {
  historyData: AvailabilitySnapshot[]
  isLoading: boolean
}

// ─── Date helpers ───────────────────────────────────────────

function toISO(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

function todayISO(): string {
  return toISO(new Date())
}

// ─── Quick-select date ranges ───────────────────────────────

type QuickRange = 'today' | '7d' | '30d' | 'month'

function getQuickRange(key: QuickRange): { from: string; to: string } {
  const today = new Date()
  switch (key) {
    case 'today':
      return { from: todayISO(), to: todayISO() }
    case '7d':
      return { from: toISO(subDays(today, 7)), to: todayISO() }
    case '30d':
      return { from: toISO(subDays(today, 30)), to: todayISO() }
    case 'month':
      return { from: toISO(startOfMonth(today)), to: todayISO() }
  }
}

// ─── Sub-tab types ──────────────────────────────────────────

type SubTab = 'overall' | 'by_client' | 'by_workshop' | 'by_make'

// ─── Metric card ────────────────────────────────────────────

function MetricCard({
  title,
  value,
  sub,
  color,
  icon: Icon,
}: {
  title: string
  value: string
  sub?: string
  color: string
  icon: React.ElementType
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
          <Icon size={20} className={color} />
        </div>
        <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

// ─── Breakdown helpers ──────────────────────────────────────

interface BreakdownRow {
  name: string
  total: number
  available: number
  in_workshop: number
  availability_pct: number
}

function computeBreakdown(trucks: Truck[], groupBy: (t: Truck) => string): BreakdownRow[] {
  const map: Record<string, { total: number; available: number; in_workshop: number }> = {}
  for (const t of trucks) {
    const key = groupBy(t) || 'Unknown'
    if (!map[key]) map[key] = { total: 0, available: 0, in_workshop: 0 }
    map[key].total++
    if (t.is_available === 1) map[key].available++
    else map[key].in_workshop++
  }
  return Object.entries(map)
    .map(([name, v]) => ({
      name,
      ...v,
      availability_pct: v.total > 0 ? Math.round((v.available / v.total) * 10000) / 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}

// ═════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════

export default function AvailabilityReport({
  historyData,
  isLoading: dashLoading,
}: AvailabilityReportProps) {
  // ── Fetch all trucks for breakdown tabs ─────────────────────
  const { trucks: allTrucks } = useTrucks()

  // ── Sub-tab state ─────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<SubTab>('overall')

  // ── Date range state ──────────────────────────────────────
  const defaultRange = getQuickRange('30d')
  const [fromDate, setFromDate] = useState(defaultRange.from)
  const [toDate, setToDate] = useState(defaultRange.to)
  const [activeQuick, setActiveQuick] = useState<QuickRange | null>('30d')

  const applyQuick = (key: QuickRange) => {
    const range = getQuickRange(key)
    setFromDate(range.from)
    setToDate(range.to)
    setActiveQuick(key)
  }

  const handleFromChange = (v: string) => { setFromDate(v); setActiveQuick(null) }
  const handleToChange = (v: string) => { setToDate(v); setActiveQuick(null) }

  // ── Filter snapshot data by date range ────────────────────
  const filteredSnapshots = useMemo(() => {
    return [...historyData]
      .filter((s) => s.snapshot_date >= fromDate && s.snapshot_date <= toDate)
      .sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date))
  }, [historyData, fromDate, toDate])

  // ── Summary metrics from filtered snapshots ───────────────
  const metrics = useMemo(() => {
    if (filteredSnapshots.length === 0) {
      return { avg: 0, best: null as AvailabilitySnapshot | null, worst: null as AvailabilitySnapshot | null }
    }
    const sum = filteredSnapshots.reduce((s, r) => s + r.availability_pct, 0)
    const avg = Math.round((sum / filteredSnapshots.length) * 100) / 100
    const best = filteredSnapshots.reduce((a, b) => (b.availability_pct > a.availability_pct ? b : a))
    const worst = filteredSnapshots.reduce((a, b) => (b.availability_pct < a.availability_pct ? b : a))
    return { avg, best, worst }
  }, [filteredSnapshots])

  // ── Breakdowns ────────────────────────────────────────────
  const byClient = useMemo(() => computeBreakdown(allTrucks, (t) => t.client_name), [allTrucks])
  const byWorkshop = useMemo(
    () => computeBreakdown(
      allTrucks.filter((t) => t.workshop_location),
      (t) => t.workshop_location ?? 'Unknown',
    ),
    [allTrucks],
  )
  const byMake = useMemo(
    () => computeBreakdown(allTrucks, (t) => normalizeMake(t.make)),
    [allTrucks],
  )

  // ── Quick button config ───────────────────────────────────
  const quickButtons: { key: QuickRange; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: '7d',    label: 'Last 7 Days' },
    { key: '30d',   label: 'Last 30 Days' },
    { key: 'month', label: 'This Month' },
  ]

  // ── Sub-tab config ────────────────────────────────────────
  const subTabs: { key: SubTab; label: string }[] = [
    { key: 'overall',     label: 'Overall' },
    { key: 'by_client',   label: 'By Client' },
    { key: 'by_workshop', label: 'By Workshop' },
    { key: 'by_make',     label: 'By Make' },
  ]

  // ── Availability bar colour helper ────────────────────────
  const barColor = (pct: number) => {
    if (pct >= 90) return 'bg-green-500'
    if (pct >= 70) return 'bg-amber-500'
    return 'bg-red-500'
  }

  // ═════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════

  return (
    <div>
      {/* ═══ PAGE HEADER ═════════════════════════════════════ */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Availability Report</h1>
        <p className="text-sm text-gray-500">
          Historical fleet availability analysis with trend breakdowns
        </p>
      </div>

      {/* ═══ SUB-TAB NAVIGATION ═══════════════════════════════ */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        {subTabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
              ${activeTab === key
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ═══ OVERALL TAB ═════════════════════════════════════ */}
      {activeTab === 'overall' && (
        <>
          {/* Date range selector */}
          <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
            <label className="text-sm font-medium text-gray-700 mb-3 block">
              Select Date Range
            </label>
            <div className="flex gap-2 mb-4 flex-wrap">
              {quickButtons.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => applyQuick(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${activeQuick === key
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-4 items-end flex-wrap">
              <div>
                <label htmlFor="report-from" className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  id="report-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromChange(e.target.value)}
                  max={toDate}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor="report-to" className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  id="report-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => handleToChange(e.target.value)}
                  min={fromDate}
                  max={todayISO()}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm
                             focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <MetricCard
              title="Avg Availability"
              value={filteredSnapshots.length > 0 ? metrics.avg + '%' : '—'}
              sub={`Over ${filteredSnapshots.length} day${filteredSnapshots.length !== 1 ? 's' : ''}`}
              color={filteredSnapshots.length > 0 ? getAvailabilityColor(metrics.avg) : 'text-gray-400'}
              icon={TrendingUp}
            />
            <MetricCard
              title="Best Day"
              value={metrics.best ? metrics.best.availability_pct + '%' : '—'}
              sub={metrics.best ? formatDate(metrics.best.snapshot_date) : 'No data'}
              color="text-green-600"
              icon={Award}
            />
            <MetricCard
              title="Worst Day"
              value={metrics.worst ? metrics.worst.availability_pct + '%' : '—'}
              sub={metrics.worst ? formatDate(metrics.worst.snapshot_date) : 'No data'}
              color="text-red-600"
              icon={TrendingDown}
            />
          </div>

          {/* Availability trend chart */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-1">
              Availability Trend — {formatDate(fromDate)} to {formatDate(toDate)}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Fleet availability percentage and in-workshop truck count
            </p>

            {dashLoading && filteredSnapshots.length === 0 ? (
              <div className="h-[320px] flex items-center justify-center text-gray-400 gap-2">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-sm">Loading chart data…</span>
              </div>
            ) : filteredSnapshots.length === 0 ? (
              <div className="h-[320px] flex items-center justify-center text-gray-400 text-sm">
                No availability data for the selected date range
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={filteredSnapshots}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                  <XAxis
                    dataKey="snapshot_date"
                    tickFormatter={(val: string) => {
                      try { return format(new Date(val), 'dd MMM') }
                      catch { return val }
                    }}
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={[0, 100]}
                    tickFormatter={(v: number) => v + '%'}
                    tick={{ fontSize: 11, fill: '#9CA3AF' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    domain={[0, 'auto']}
                    tick={{ fontSize: 11, fill: '#D97706' }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: 'In Workshop',
                      angle: 90,
                      position: 'insideRight',
                      style: { fontSize: 11, fill: '#D97706' },
                    }}
                  />
                  <RechartTooltip
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      fontSize: '12px',
                    }}
                    labelFormatter={(label: string) => {
                      try { return format(new Date(label), 'dd MMM yyyy') }
                      catch { return label }
                    }}
                    formatter={(value: number, name: string) => {
                      if (name === 'availability_pct') return [value + '%', 'Availability']
                      if (name === 'trucks_in_workshop') return [value, 'In Workshop']
                      return [value, name]
                    }}
                  />
                  <ReferenceLine
                    yAxisId="left"
                    y={85}
                    stroke="#EF4444"
                    strokeDasharray="5 5"
                    label={{
                      value: 'Target 85%',
                      position: 'insideTopRight',
                      fontSize: 11,
                      fill: '#EF4444',
                    }}
                  />
                  <Area
                    yAxisId="left"
                    dataKey="availability_pct"
                    fill="#EFF6FF"
                    stroke="none"
                    type="monotone"
                  />
                  <Line
                    yAxisId="left"
                    dataKey="availability_pct"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#3B82F6' }}
                    type="monotone"
                    name="availability_pct"
                  />
                  <Line
                    yAxisId="right"
                    dataKey="trucks_in_workshop"
                    stroke="#F59E0B"
                    strokeWidth={1.5}
                    strokeDasharray="4 4"
                    dot={false}
                    type="monotone"
                    name="trucks_in_workshop"
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="line"
                    formatter={(value: string) => {
                      if (value === 'availability_pct') return 'Fleet Availability %'
                      if (value === 'trucks_in_workshop') return 'Trucks In Workshop'
                      return value
                    }}
                    wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}

      {/* ═══ BY CLIENT TAB ═══════════════════════════════════ */}
      {activeTab === 'by_client' && (
        <BreakdownView
          title="Availability by Client"
          subtitle="Fleet availability breakdown per client group"
          data={byClient}
          barColor={barColor}
          getAvailabilityColor={getAvailabilityColor}
        />
      )}

      {/* ═══ BY WORKSHOP TAB ═════════════════════════════════ */}
      {activeTab === 'by_workshop' && (
        <BreakdownView
          title="Trucks by Workshop Location"
          subtitle="Distribution of in-workshop trucks by assigned workshop"
          data={byWorkshop}
          barColor={barColor}
          getAvailabilityColor={getAvailabilityColor}
          emptyMessage="No trucks currently assigned to a workshop location"
        />
      )}

      {/* ═══ BY MAKE TAB ═════════════════════════════════════ */}
      {activeTab === 'by_make' && (
        <BreakdownView
          title="Availability by Vehicle Make"
          subtitle="Fleet availability breakdown per vehicle manufacturer"
          data={byMake}
          barColor={barColor}
          getAvailabilityColor={getAvailabilityColor}
        />
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════
// BREAKDOWN VIEW (reusable for Client / Workshop / Make tabs)
// ═════════════════════════════════════════════════════════════

function BreakdownView({
  title,
  subtitle,
  data,
  barColor,
  getAvailabilityColor: getAvailColor,
  emptyMessage,
}: {
  title: string
  subtitle: string
  data: BreakdownRow[]
  barColor: (pct: number) => string
  getAvailabilityColor: (pct: number) => string
  emptyMessage?: string
}) {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">
        {emptyMessage || 'No data available'}
      </div>
    )
  }

  const totals = data.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      available: acc.available + r.available,
      in_workshop: acc.in_workshop + r.in_workshop,
    }),
    { total: 0, available: 0, in_workshop: 0 },
  )
  const totalPct = totals.total > 0 ? Math.round((totals.available / totals.total) * 10000) / 100 : 0

  return (
    <>
      {/* Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
        <h2 className="text-base font-semibold text-gray-800 mb-1">{title}</h2>
        <p className="text-xs text-gray-500 mb-4">{subtitle}</p>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: '#374151' }}
              axisLine={false}
              tickLine={false}
              width={100}
            />
            <RechartTooltip
              contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '12px' }}
              formatter={(value: number, name: string) => {
                if (name === 'available') return [value, 'Available']
                if (name === 'in_workshop') return [value, 'In Workshop']
                return [value, name]
              }}
            />
            <Legend
              verticalAlign="bottom"
              formatter={(value: string) => {
                if (value === 'available') return 'Available'
                if (value === 'in_workshop') return 'In Workshop'
                return value
              }}
              wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
            />
            <Bar dataKey="available" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} name="available" />
            <Bar dataKey="in_workshop" stackId="a" fill="#F59E0B" radius={[0, 4, 4, 0]} name="in_workshop" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3">Name</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-3">Total</th>
                <th className="text-center text-xs font-semibold text-green-600 uppercase tracking-wide px-3 py-3">Available</th>
                <th className="text-center text-xs font-semibold text-amber-600 uppercase tracking-wide px-3 py-3">Workshop</th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-3">Avail %</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3 min-w-[120px]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.map((row) => (
                <tr key={row.name} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-2.5 font-medium text-gray-800 text-sm">{row.name}</td>
                  <td className="px-3 py-2.5 text-center text-gray-700 tabular-nums text-sm">{row.total}</td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-sm">
                    <span className={row.available > 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}>{row.available}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center tabular-nums text-sm">
                    <span className={row.in_workshop > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}>{row.in_workshop}</span>
                  </td>
                  <td className={`px-3 py-2.5 text-center font-semibold tabular-nums text-sm ${getAvailColor(row.availability_pct)}`}>
                    {row.availability_pct}%
                  </td>
                  <td className="px-5 py-2.5">
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className={`${barColor(row.availability_pct)} rounded-full h-1.5 transition-all duration-500`}
                        style={{ width: `${Math.min(row.availability_pct, 100)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}

              {/* Totals row */}
              <tr className="bg-gray-50 font-bold border-t-2 border-gray-200">
                <td className="px-5 py-2.5 text-gray-800 text-sm">TOTAL</td>
                <td className="px-3 py-2.5 text-center text-gray-800 tabular-nums text-sm">{totals.total}</td>
                <td className="px-3 py-2.5 text-center text-green-700 tabular-nums text-sm">{totals.available}</td>
                <td className="px-3 py-2.5 text-center text-amber-700 tabular-nums text-sm">{totals.in_workshop}</td>
                <td className={`px-3 py-2.5 text-center tabular-nums text-sm ${getAvailColor(totalPct)}`}>
                  {totalPct}%
                </td>
                <td className="px-5 py-2.5">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className={`${barColor(totalPct)} rounded-full h-1.5 transition-all duration-500`}
                      style={{ width: `${Math.min(totalPct, 100)}%` }}
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
